import { redirect, type LoaderFunctionArgs } from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

const RUNS_PAGE_SIZE = 10;
const RESULTS_PAGE_SIZE = 10;

type TestRunRow = Database["public"]["Tables"]["test_runs"]["Row"];
type TestResultRow = Database["public"]["Tables"]["test_results"]["Row"];
type TestDataSetRow = Database["public"]["Tables"]["test_data_sets"]["Row"];
type TestDataEntryRow =
	Database["public"]["Tables"]["test_data_entries"]["Row"];
type JudgeRow = Database["public"]["Tables"]["judges"]["Row"];
type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];
type PgflowRunStatusRow =
	Database["public"]["Views"]["pgflow_run_statuses"]["Row"];
type TestResultRowWithEntry = TestResultRow & {
	test_data_entries:
		| TestDataEntryRow
		| TestDataEntryRow[]
		| MinimalEntry
		| MinimalEntry[]
		| null;
};

export type SupabaseFromClient = Pick<typeof supabase, "from">;

export const fetchProjectTestRuns = async (
	projectId: string,
	page: number,
	client: SupabaseFromClient = supabase,
) => {
	const { data, count, error } = await client
		.from("test_runs")
		.select("*", { count: "exact" })
		.eq("project_id", projectId)
		.order("created_at", { ascending: false })
		.range((page - 1) * RUNS_PAGE_SIZE, page * RUNS_PAGE_SIZE - 1)
		.returns<TestRunRow[]>();

	if (error) {
		throw error;
	}

	return {
		testRuns: data ?? [],
		totalCount: count ?? 0,
	} as const;
};

type MinimalEntry = Pick<TestDataEntryRow, "id" | "input" | "expected_output">;

type ResultWithEntry = TestResultRow & {
	test_data_entries: MinimalEntry | null;
};

type ResultWithRobustness = ResultWithEntry & {
	deepTestResults: ResultWithEntry[];
	robustness_count: number;
	robustness_passed: number;
};

type RobustnessScoreRow =
	Database["public"]["Tables"]["robustness_score"]["Row"];

const normalizeEntry = (
	entry:
		| TestDataEntryRow
		| MinimalEntry
		| TestDataEntryRow[]
		| MinimalEntry[]
		| null,
): MinimalEntry | null => {
	if (!entry) return null;
	if (Array.isArray(entry)) {
		const first = entry[0];
		if (!first) return null;
		return {
			id: first.id,
			input: first.input,
			expected_output: first.expected_output,
		};
	}
	return {
		id: entry.id,
		input: entry.input,
		expected_output: entry.expected_output,
	};
};

const normalizeResultWithEntry = (
	result: TestResultRowWithEntry,
): ResultWithEntry => ({
	...result,
	test_data_entries: normalizeEntry(result.test_data_entries),
});

const parsePage = (value: string | null, defaultValue = 1) => {
	const num = Number(value ?? defaultValue);
	return Number.isNaN(num) || num < 1 ? defaultValue : Math.floor(num);
};

export const testRunListLoader = async ({ request }: LoaderFunctionArgs) => {
	const url = new URL(request.url);
	const projectId = url.searchParams.get("projectId");
	if (!projectId) {
		throw new Response("projectId is required", { status: 400 });
	}

	const dataSetIdParam = url.searchParams.get("dataSetId");
	const page = parsePage(url.searchParams.get("page"));

	const {
		data: { session },
	} = await supabase.auth.getSession();

	if (!session?.user) {
		throw redirect("/auth");
	}

	const { data: testDataSetsData, error: testDataSetsError } = await supabase
		.from("test_data_sets")
		.select("*")
		.order("created_at", { ascending: false })
		.returns<TestDataSetRow[]>();

	if (testDataSetsError) {
		throw testDataSetsError;
	}

	const testDataSets = testDataSetsData ?? [];
	const selectedDataSetId =
		dataSetIdParam && testDataSets.some((set) => set.id === dataSetIdParam)
			? dataSetIdParam
			: (testDataSets[0]?.id ?? null);

	let dataSetEntryCount = 0;
	if (selectedDataSetId) {
		const { count, error } = await supabase
			.from("test_data_entries")
			.select("id", { count: "exact", head: true })
			.eq("test_data_set_id", selectedDataSetId);

		if (error) {
			throw error;
		}

		dataSetEntryCount = count ?? 0;
	}

	type TestRunWithMetadata = TestRunRow & {
		profiles?: { display_name: string | null } | null;
		judges?: { title: string; model: string | null } | null;
		pgflow_status?: string | null;
		pgflow_remaining_steps?: number | null;
		pgflow_started_at?: string | null;
		pgflow_completed_at?: string | null;
		pgflow_latest_step_slug?: string | null;
		pgflow_latest_step_status?: string | null;
		pgflow_last_error?: string | null;
		pgflow_active_worker_id?: string | null;
	};

	let testRuns: TestRunWithMetadata[] = [];
	let totalCount = 0;

	const runResults = await fetchProjectTestRuns(projectId, page);
	totalCount = runResults.totalCount;
	testRuns = runResults.testRuns;

	if (testRuns.length > 0) {
		const judgeIds = [
			...new Set(
				testRuns
					.map((run) => run.judge_id)
					.filter((id): id is string => Boolean(id)),
			),
		];
		const userIds = [
			...new Set(testRuns.map((run) => run.user_id).filter(Boolean)),
		];
		const runIds = testRuns.map((run) => run.id);

		const [judgesLookup, profilesLookup, pgflowStatusesLookup] =
			await Promise.all([
				judgeIds.length
					? supabase
							.from("judges")
							.select("id, title, model")
							.in("id", judgeIds)
							.returns<Array<Pick<JudgeRow, "id" | "title" | "model">>>()
					: Promise.resolve({ data: [], error: null }),
				userIds.length
					? supabase
							.from("profiles")
							.select("user_id, display_name")
							.in("user_id", userIds)
							.returns<Array<Pick<ProfileRow, "user_id" | "display_name">>>()
					: Promise.resolve({ data: [], error: null }),
				runIds.length
					? supabase
							.from("pgflow_run_statuses")
							.select("*")
							.in("test_run_id", runIds)
							.returns<PgflowRunStatusRow[]>()
					: Promise.resolve({ data: [], error: null }),
			]);

		if (judgesLookup.error) {
			throw judgesLookup.error;
		}
		if (profilesLookup.error) {
			throw profilesLookup.error;
		}
		if (pgflowStatusesLookup.error) {
			throw pgflowStatusesLookup.error;
		}

		const judgeMap = new Map(
			(judgesLookup.data ?? []).map((judge) => [judge.id, judge as JudgeRow]),
		);
		const profileMap = new Map(
			(profilesLookup.data ?? []).map((profile) => [
				profile.user_id,
				profile.display_name ?? null,
			]),
		);
		const pgflowMap = new Map(
			(pgflowStatusesLookup.data ?? [])
				.filter((row): row is PgflowRunStatusRow & { test_run_id: string } =>
					Boolean(row.test_run_id),
				)
				.map((row) => [row.test_run_id!, row]),
		);

		testRuns = testRuns.map((run) => {
			const judgeInfo = run.judge_id ? judgeMap.get(run.judge_id) : undefined;
			const ownerName = profileMap.get(run.user_id) ?? null;
			const pgflow = pgflowMap.get(run.id ?? "");
			return {
				...run,
				profiles: ownerName ? { display_name: ownerName } : null,
				judges: judgeInfo
					? { title: judgeInfo.title, model: judgeInfo.model }
					: null,
				pgflow_status: pgflow?.pgflow_status ?? null,
				pgflow_remaining_steps: pgflow?.remaining_steps ?? null,
				pgflow_started_at: pgflow?.started_at ?? null,
				pgflow_completed_at: pgflow?.completed_at ?? null,
				pgflow_latest_step_slug: pgflow?.latest_step_slug ?? null,
				pgflow_latest_step_status: pgflow?.latest_step_status ?? null,
				pgflow_last_error: pgflow?.last_error_message ?? null,
				pgflow_active_worker_id: pgflow?.active_worker_id ?? null,
			};
		});
	}

	// Fetch judges - RLS handles access control (admins see all, users see own/public)
	const { data: judgesData, error: judgesError } = await supabase
		.from("judges")
		.select("*")
		.order("created_at", { ascending: false })
		.returns<JudgeRow[]>();

	if (judgesError) {
		throw judgesError;
	}

	return {
		projectId,
		page,
		pageSize: RUNS_PAGE_SIZE,
		testDataSets,
		selectedDataSetId,
		dataSetEntryCount,
		testRuns,
		totalCount,
		judges: judgesData ?? [],
	} as const;
};

export const testRunResultsLoader = async ({ request }: LoaderFunctionArgs) => {
	const url = new URL(request.url);
	const testRunId = url.searchParams.get("testRunId");
	if (!testRunId) {
		throw new Response("testRunId is required", { status: 400 });
	}

	const page = parsePage(url.searchParams.get("page"));

	const {
		data: { session },
	} = await supabase.auth.getSession();

	if (!session?.user) {
		throw redirect("/auth");
	}

	// Fetch primary run first to determine if we're viewing an adversarial run
	const { data: primaryRun, error: primaryRunError } = await supabase
		.from("test_runs")
		.select("*")
		.eq("id", testRunId)
		.maybeSingle<TestRunRow>();

	if (primaryRunError) {
		throw primaryRunError;
	}
	if (!primaryRun) {
		throw new Response("Test run not found", {
			status: 404,
		});
	}

	// Determine which run to fetch results for
	// If this is an adversarial run, use its own results so users see the
	// adversarial outputs instead of the baseline primary run.
	const resultsRunId =
		primaryRun.type === "adversarial"
			? testRunId
			: primaryRun.primary_run_required_id ?? testRunId;

	const {
		data: resultsData,
		count,
		error: resultsError,
	} = await supabase
		.from("test_results")
		.select(
			`*,
      test_data_entries (id, input, expected_output)
    `,
			{ count: "exact" },
		)
		.eq("test_run_id", resultsRunId)
		.order("created_at", { ascending: true })
		.range((page - 1) * RESULTS_PAGE_SIZE, page * RESULTS_PAGE_SIZE - 1)
		.returns<TestResultRowWithEntry[]>();

	if (resultsError) {
		throw resultsError;
	}

	const { data: statsData, error: statsError } = await supabase
		.from("test_results")
		.select("correctness")
		.eq("test_run_id", resultsRunId)
		.returns<Array<Pick<TestResultRow, "correctness">>>();

	if (statsError) {
		throw statsError;
	}

	// Get the run record we're actually viewing
	const { data: viewedRun, error: viewedRunError } = await supabase
		.from("test_runs")
		.select("*")
		.eq("id", testRunId)
		.maybeSingle<TestRunRow>();

	if (viewedRunError) {
		throw viewedRunError;
	}
	if (!viewedRun) {
		throw new Response("Test run not found", {
			status: 404,
		});
	}

	// Get the test set for the run we're fetching results from
	let primaryTestSet: TestDataSetRow | null = null;
	const targetRunForResults = viewedRun.primary_run_required_id
		? await supabase
				.from("test_runs")
				.select("*")
				.eq("id", viewedRun.primary_run_required_id)
				.maybeSingle<TestRunRow>()
		: { data: viewedRun, error: null };

	if (targetRunForResults.error) {
		throw targetRunForResults.error;
	}

	const targetRun = targetRunForResults.data;
	if (targetRun?.test_data_set_id) {
		const { data: primarySetData, error: primarySetError } = await supabase
			.from("test_data_sets")
			.select("*")
			.eq("id", targetRun.test_data_set_id)
			.maybeSingle<TestDataSetRow>();

		if (primarySetError) {
			throw primarySetError;
		}

		primaryTestSet = primarySetData;
	}

	let derivativeTestSet: TestDataSetRow | null = null;
	let deepTestRun: TestRunRow | null = null;

	// Check if we're viewing an adversarial run (has primary_run_required_id)
	if (viewedRun.primary_run_required_id && viewedRun.test_data_set_id) {
		// We're viewing an adversarial run - set it as the adversarial test run
		deepTestRun = viewedRun;

		// Get the adversarial test set
		const { data: derivativeSetData, error: derivativeSetError } =
			await supabase
				.from("test_data_sets")
				.select("*")
				.eq("id", viewedRun.test_data_set_id)
				.maybeSingle<TestDataSetRow>();

		if (derivativeSetError) {
			throw derivativeSetError;
		}

		derivativeTestSet = derivativeSetData;
		// primaryTestSet is already set to the parent test set from earlier
	} else if (primaryTestSet) {
		// We're viewing a primary run - fetch adversarial if exists
		const { data: derivativeSet, error: derivativeSetError } = await supabase
			.from("test_data_sets")
			.select("*")
			.eq("parent_id", primaryTestSet.id)
			.eq("type", "adversarial")
			.order("created_at", { ascending: false })
			.maybeSingle<TestDataSetRow>();

		if (derivativeSetError) {
			console.error("Failed to load adversarial test set:", derivativeSetError);
			derivativeTestSet = null;
		} else {
			derivativeTestSet = derivativeSet;
		}

		if (derivativeTestSet) {
			const { data: deepRun, error: deepRunError } = await supabase
				.from("test_runs")
				.select("*")
				.eq("test_data_set_id", derivativeTestSet.id)
				.eq("project_id", primaryRun.project_id)
				.eq("type", "adversarial")
				.eq("status", "Complete")
				.order("created_at", { ascending: false })
				.maybeSingle<TestRunRow>();

			if (deepRunError) {
				throw deepRunError;
			}

			deepTestRun = deepRun;
		}
	}

	const results: ResultWithRobustness[] = (resultsData ?? []).map((result) => ({
		...normalizeResultWithEntry(result),
		deepTestResults: [],
		robustness_count: 0,
		robustness_passed: 0,
	}));

	const primaryEntryToResult = new Map<string, ResultWithRobustness>();
	results.forEach((result) => {
		primaryEntryToResult.set(result.test_data_entry_id, result);
	});

	const derivativeEntryParentMap = new Map<string, string>();

	if (derivativeTestSet && deepTestRun) {
		const { data: derivativeEntries, error: derivativeEntriesError } =
			await supabase
				.from("test_data_entries")
				.select("id, parent_id")
				.eq("test_data_set_id", derivativeTestSet.id)
				.returns<Array<Pick<TestDataEntryRow, "id" | "parent_id">>>();

		if (derivativeEntriesError) {
			throw derivativeEntriesError;
		}

		(derivativeEntries ?? []).forEach((entry) => {
			if (entry.parent_id) {
				derivativeEntryParentMap.set(entry.id, entry.parent_id);
			}
		});

		const derivativeIds = Array.from(derivativeEntryParentMap.keys());

		if (derivativeIds.length > 0) {
			const { data: deepResultsRaw, error: deepResultsError } = await supabase
				.from("test_results")
				.select(
					`*,
          test_data_entries (id, input, expected_output)
        `,
				)
				.eq("test_run_id", deepTestRun.id)
				.in("test_data_entry_id", derivativeIds)
				.order("created_at", { ascending: true })
				.returns<TestResultRowWithEntry[]>();

			if (deepResultsError) {
				throw deepResultsError;
			}

			const deepResults: ResultWithEntry[] = (deepResultsRaw ?? []).map(
				(result) => normalizeResultWithEntry(result),
			);

			deepResults.forEach((deepResult) => {
				const parentEntryId = derivativeEntryParentMap.get(
					deepResult.test_data_entry_id,
				);
				if (!parentEntryId) return;
				const parentResult = primaryEntryToResult.get(parentEntryId);
				if (!parentResult) return;
				parentResult.deepTestResults.push(deepResult);
			});

			results.forEach((result) => {
				if (result.deepTestResults.length === 0) return;
				result.robustness_count = result.deepTestResults.length;
				result.robustness_passed = result.deepTestResults.filter(
					(adversarial) => adversarial.correctness === 100,
				).length;
			});
		}
	}

	const deepTestInfo =
		primaryTestSet && derivativeTestSet && deepTestRun
			? {
					primaryTestSet,
					derivativeTestSet,
					deepTestRun,
				}
			: null;

	const totalResults = statsData?.length ?? 0;
	const correct = statsData?.filter((r) => r.correctness === 100).length ?? 0;
	const partial =
		statsData?.filter(
			(r) => r.correctness !== null && r.correctness > 0 && r.correctness < 100,
		).length ?? 0;
	const incorrect = statsData?.filter((r) => r.correctness === 0).length ?? 0;

	const percent = (value: number) =>
		totalResults === 0 ? 0 : Math.round((value / totalResults) * 100);

	let robustnessScore: Pick<RobustnessScoreRow, "robust_accuracy" | "metrics"> | null =
		null;
	if (viewedRun.type === "adversarial") {
		const { data: robustnessRow, error: robustnessError } = await supabase
			.from("robustness_score")
			.select("robust_accuracy, metrics")
			.eq("derivative_test_run", viewedRun.id)
			.maybeSingle<Pick<RobustnessScoreRow, "robust_accuracy" | "metrics">>();

		if (robustnessError) {
			throw robustnessError;
		}

		robustnessScore = robustnessRow ?? null;
	}

	return {
		page,
		pageSize: RESULTS_PAGE_SIZE,
		totalCount: count ?? 0,
		testResults: results,
		stats: {
			correct: percent(correct),
			partial: percent(partial),
			incorrect: percent(incorrect),
		},
		deepTestInfo,
		robustnessScore,
		viewedRun,
		viewedTestSet: primaryTestSet,
		primaryRun: viewedRun.primary_run_required_id ? primaryRun : null,
	} as const;
};

export type TestRunListLoaderData = Awaited<
	ReturnType<typeof testRunListLoader>
>;
export type TestRunResultsLoaderData = Awaited<
	ReturnType<typeof testRunResultsLoader>
>;
